import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { store } from "../../app/store";

// mui components
import { Box, Stack, Tab, Tabs } from "@mui/material";

// custom components
import SecondaryButton from "../../components/buttons/secondary-button";
import CustomLayout from "../../components/layout";
import LoanSummaryCard from "../../components/card/loan-summary";
import TabPanel from "../../components/tab-panel";

import LockInfoCard from "../../components/card/lock-info";
import ConditionTrackingCard from "../../components/card/condition-tracking";
import ChangeRequestCard from "../../components/card/change-request";
import DocumentsCard from "../../components/card/documents";
import DocumentsTrackerCard from "../../components/card/documents-tracker";
import LoanDocumentsCard from "../../components/card/loan-documents";
import ImportantDatesCard from "../../components/card/important-dates";
import AppraisalsCard from "../../components/card/appraisals";
import ClosingInformationCard from "../../components/card/closing-information";
import RequestLockCard from "../../components/card/request-lock";
import LoanContactCard from "../../components/card/loan-contact";

import TemporaryDrawer from "../../components/temporary-drawer";

// custom modal
import LoadingDataModal from "../../components/modal/loading-data-modal";
import ProcessingModal from "../../components/modal/processing-modal";
import SuccessModal from "../../components/modal/success-modal";
import ErrorModal from "../../components/modal/error-modal";
import NavigationModal from "../../components/modal/navigation-modal";
import CommanErrorModal from "../../components/modal/common-error-modal";
import { useErrorMessages } from "../../utils/error-messages.constants";

// api slice
import { useGetLoanSummaryQuery } from "../../features/loanSummary/loanSummaryApiSlice";
import { useFieldMutation } from "../../features/field-values/fieldValueApiSlice";
import { useEnhancedConditionsDocumentsListByCategoryMutation } from "../../features/fetch-enhanced-conditions-documents-list-by-category/fetchEnhancedConditionsDocumentsListByCatgoryApiSlice";
import { useUnderwritingConditionMutation } from "../../features/underwriting-conditions/underwritingApiSlice";
import { usePreliminaryConditionMutation } from "../../features/preliminary-conditions/preliminaryApiSlice";
import { usePostClosingConditionMutation } from "../../features/post-closing-conditions/postClosingApiSlice";

// reducer slices
import {
	selectUserDetails,
	selectSelectedOrganization,
} from "../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../features/admin/impersonated-user/impersonatedUserSlice";
import {
	selectEnhancedConditionStatus,
	setEnhancedConditionStatus,
} from "../../features/enhanced-condition-status/enhancedConditionStatusSlice";

// helper utils
import { LOAN_CHANNEL_CONSTANTS } from "../../utils/common-constants";
import { selectedUserWithLoanChannel } from "../../utils/common-helpers";
import { filterValue } from "../../utils/filter-value";
import { getEnhancedConditionsDocumentsListByCategory } from "../../app/services/enhanced-conditions-documents-lists-by-category";
import { getUnderwritingConditions } from "../../app/services/underwriting-conditions";
import { getPreliminaryConditions } from "../../app/services/preliminary-conditions";
import { getPostClosingConditions } from "../../app/services/post-closing-conditions";

// custom styles
import styles from "./index.module.scss";

// custom mui styles
import "./mui-styles.scss";

const LoanSummary = () => {
	const { getErrorMessage } = useErrorMessages();

	const params = useParams();
	const navigate = useNavigate();
	const location = useLocation();

	const normalizeLoanGuid = (value) => {
		const text = String(value ?? "").trim();
		return text && text !== "undefined" && text !== "null" ? text : "";
	};

	const [tabValue, setTabValue] = useState(false);

	useEffect(() => {
		if (typeof location.state?.tabValue === "number") {
			setTabValue(location.state.tabValue);
		}
	}, [location.state?.tabValue]);

	const [selectedCondition, setSelectedCondition] = useState("");

	const [processingText, setProcessingText] = useState("");
	const [successText, setSuccessText] = useState("");
	const [documentTrackerUploadProgress, setDocumentTrackerUploadProgress] =
		useState(null);
	const [isConditionStatusLoaded, setIsConditionStatusLoaded] =
		useState(false);
	const [conditionTypeOptions, setConditionTypeOptions] = useState([]);
	const lastLoadedConditionOptionsKeyRef = useRef("");
	const isLoadingConditionOptionsRef = useRef(false);

	const [drawerType, setDrawerType] = useState("");
	const [drawerOpen, setDrawerOpen] = useState({});
	const [drawerData, setDrawerData] = useState(null);

	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);

	const handleOpenDrawer = () => {
		setDrawerOpen({
			right: true,
		});
	};

	const handleCloseDrawer = () => {
		setDrawerOpen({
			right: false,
		});
		setDrawerType("");
		setDrawerData(null);
	};

	const [processingModalVisible, setProcessingModalVisible] = useState(false);

	const handleOpenProcessingModal = () => {
		setProcessingModalVisible(true);
	};

	const handleCloseProcessingModal = () => {
		setProcessingModalVisible(false);
	};

	const [successModalVisible, setSuccessModalVisible] = useState(false);

	const handleOpenSuccessModal = () => {
		setSuccessModalVisible(true);
	};

	const handleCloseSuccessModal = () => {
		setSuccessModalVisible(false);
	};

	const [errorModalText, setErrorModalText] = useState("");
	const [errorModalTitle, setErrorModalTitle] = useState("");
	const [errorModalVisible, setErrorModalVisible] = useState(false);
	const handleOpenErrorModal = () => {
		setErrorModalVisible(true);
	};

	const handleCloseErrorModal = () => {
		setErrorModalVisible(false);
	};

	const [commonErrorData, setCommonErrorData] = useState(null);
	const [commonErrorModalVisible, setCommonErrorModalVisible] = useState(false);
	const handleOpenCommonErrorModal = (errorInput, errorDetail) => {
		if (typeof errorInput === "string") {
			setCommonErrorData(getErrorMessage(errorInput, errorDetail));
		} else if (
			errorInput &&
			(typeof errorInput === "object" || typeof errorInput === "function")
		) {
			const fallbackError = getErrorMessage("DEFAULT_ERROR");
			setCommonErrorData({
				code: errorInput?.code || fallbackError.code,
				title: errorInput?.title || fallbackError.title,
				message: errorInput?.message || fallbackError.message,
				error_response: errorInput?.error_response ?? null,
			});
		} else {
			setCommonErrorData(getErrorMessage("DEFAULT_ERROR"));
		}
		setCommonErrorModalVisible(true);
	};
	const handleCloseCommonErrorModal = () => {
		setCommonErrorModalVisible(false);
		setCommonErrorData(null);
	};

	const selectedOrganization = useSelector(selectSelectedOrganization);

	const { data: loanSummaryData, isLoading: loadingLoanData } =
		useGetLoanSummaryQuery({
			loanId: params?.id,
			selectedOrganization,
			userDetails,
		});

	const [loanData, setLoanData] = useState({});
	const resolvedLoanGuid = normalizeLoanGuid(
		params?.id ??
			params?.loanId ??
			loanData?.loanGuid ??
			loanData?.loan_guid ??
			loanData?.id
	);

	useEffect(() => {
		if (loanSummaryData !== undefined) {
			setLoanData(loanSummaryData?.data);
		}
	}, [loanSummaryData]);

	const [loadingDataText, setLoadingDataText] = useState("");
	const [loadingDataModal, setLoadingDataModal] = useState(false);

	const handleOpenLoadingDataModal = () => {
		setLoadingDataModal(true);
	};

	const handleCloseLoadingDataModal = () => {
		setLoadingDataModal(false);
	};

	const [navigationModalText, setNavigationModalText] = useState("");
	const [navigationModalVisible, setNavigationModalVisible] = useState(false);
	const [orgLoanChannel, setOrgLoanChannel] = useState("");

	const [hideLoan, setHideLoan] = useState(false);

	const handleOpenNavigationModal = () => {
		setNavigationModalVisible(true);
	};

	const handleCloseNavigationModal = () => {
		setNavigationModalVisible(false);
	};

	const handleNavigatePipeline = () => {
		handleCloseNavigationModal();
		navigate("/pipeline");
	};

	useEffect(() => {
		if (loadingLoanData) {
			handleOpenLoadingDataModal();
			setLoadingDataText("Setting up the loan details");
		} else {
			handleCloseLoadingDataModal();
		}
	}, [loadingLoanData]);

	useEffect(() => {
		// check if user is from the same organization and loan channel
		console.log("145 user channel:", userDetails?.organization_name);
		console.log("145 selected organization:", selectedOrganization);
		console.log("145 org loan channel:", orgLoanChannel);

		// Check if admin is impersonating a user
		const isImpersonating =
			impersonatedUser?.email !== undefined &&
			impersonatedUser?.email !== null &&
			impersonatedUser?.email !== "";

		// Use impersonated user details if available, otherwise use current user
		const effectiveUser = isImpersonating ? impersonatedUser : userDetails;

		console.log("145 is impersonating:", isImpersonating);
		console.log("145 effective user:", effectiveUser?.email);

		const isInternalUser =
			effectiveUser?.user_roles?.includes("admin") ||
			effectiveUser?.user_roles?.includes("manager") ||
			effectiveUser?.user_roles?.includes("executive") ||
			effectiveUser?.user_roles?.includes("assistant") ||
			effectiveUser?.user_roles?.includes("AE") ||
			effectiveUser?.user_roles?.includes("AM") ||
			effectiveUser?.user_roles?.includes("PA");

		// If admin is impersonating, allow access (inherit mimicked user's permissions)
		if (isImpersonating) {
			console.log("145 admin impersonating user - allowing access");
			return;
		}

		if (isInternalUser) {
			return;
		}

		if (orgLoanChannel !== undefined && orgLoanChannel !== "") {
			console.log("190 org loan channel:", orgLoanChannel);

			// Collect all org names the user belongs to
			const userOrgNames = Array.isArray(effectiveUser?.organizations)
				? effectiveUser.organizations.map((org) => org.name)
				: [];
			const primaryOrgName = effectiveUser?.organization_name;
			const childOrgName = effectiveUser?.child_org_name;

			const primaryMatch = Array.isArray(primaryOrgName)
				? primaryOrgName.includes(orgLoanChannel)
				: primaryOrgName === orgLoanChannel;

			const hasAccess =
				primaryMatch ||
				userOrgNames.includes(orgLoanChannel) ||
				childOrgName === orgLoanChannel ||
				selectedOrganization === orgLoanChannel;

			const hasAnyOrgInfo =
				(Array.isArray(primaryOrgName)
					? primaryOrgName.length > 0
					: primaryOrgName) ||
				userOrgNames.length > 0 ||
				childOrgName;

			if (hasAnyOrgInfo && !hasAccess) {
				console.log("145 organization does not match");
				setNavigationModalText("You do not have access to this loan.");
				handleOpenNavigationModal();
				setHideLoan(true);
			} else {
				setHideLoan(false);
			}
		}
	}, [userDetails, orgLoanChannel, impersonatedUser, selectedOrganization]);

	const [field] = useFieldMutation();
	const [enhancedConditionsRequest] =
		useEnhancedConditionsDocumentsListByCategoryMutation();
	const [underwritingCondition] = useUnderwritingConditionMutation();
	const [preliminaryCondition] = usePreliminaryConditionMutation();
	const [postClosingCondition] = usePostClosingConditionMutation();
	const lastLoadedFieldValuesKeyRef = useRef("");
	const isLoadingFieldValuesRef = useRef(false);

	const loadConditionTypeOptions = useCallback(
		async (enhancedConditionValue, { forceRefresh = false } = {}) => {
			if (!resolvedLoanGuid || !params?.id) return;

			const normalizedEnhancedConditionValue = String(
				enhancedConditionValue ?? ""
			)
				.trim()
				.toUpperCase();
			const requestKey =
				normalizedEnhancedConditionValue === "Y"
					? `${params?.id}:condition-options:enhanced`
					: `${params?.id}:condition-options:standard`;

			if (
				isLoadingConditionOptionsRef.current ||
				(!forceRefresh &&
					lastLoadedConditionOptionsKeyRef.current === requestKey)
			) {
				return conditionTypeOptions;
			}

			isLoadingConditionOptionsRef.current = true;
			setIsConditionStatusLoaded(false);
			setConditionTypeOptions([]);

			try {
				if (normalizedEnhancedConditionValue === "Y") {
					const conditionDocumentsResponse =
						await getEnhancedConditionsDocumentsListByCategory({
							loanGuid: params?.id,
							conditionType: "All",
							apiRequest: enhancedConditionsRequest,
							handleErrorModal: handleOpenCommonErrorModal,
						});

					const nextConditionTypeOptions =
						conditionDocumentsResponse?.data || [];
					setConditionTypeOptions(nextConditionTypeOptions);
					lastLoadedConditionOptionsKeyRef.current = requestKey;
					return nextConditionTypeOptions;
				} else {
					const [underwritingResponse, preliminaryResponse, postClosingResponse] =
						await Promise.all([
							getUnderwritingConditions({
								loanGuid: params?.id,
								apiRequest: underwritingCondition,
								handleErrorModal: handleOpenCommonErrorModal,
							}),
							getPreliminaryConditions({
								loanGuid: params?.id,
								apiRequest: preliminaryCondition,
								handleErrorModal: handleOpenCommonErrorModal,
							}),
							getPostClosingConditions({
								loanGuid: params?.id,
								apiRequest: postClosingCondition,
								handleErrorModal: handleOpenCommonErrorModal,
							}),
						]);

					const nextConditionTypeOptions = [
						...(underwritingResponse?.data ?? []),
						...(preliminaryResponse?.data ?? []),
						...(postClosingResponse?.data ?? []),
					];

					setConditionTypeOptions(nextConditionTypeOptions);
					lastLoadedConditionOptionsKeyRef.current = requestKey;
					return nextConditionTypeOptions;
				}
			} finally {
				setIsConditionStatusLoaded(true);
				isLoadingConditionOptionsRef.current = false;
			}
		},
		[
			conditionTypeOptions,
			enhancedConditionsRequest,
			handleOpenCommonErrorModal,
			params?.id,
			postClosingCondition,
			preliminaryCondition,
			resolvedLoanGuid,
			setIsConditionStatusLoaded,
			underwritingCondition,
		]
	);

	const [fieldValues, setFieldValues] = useState([]);

	const getFieldValues = useCallback(async () => {
		const requestKey = `${params?.id ?? ""}:${selectedOrganization ?? ""}`;

		if (
			isLoadingFieldValuesRef.current ||
			lastLoadedFieldValuesKeyRef.current === requestKey
		) {
			return;
		}

		isLoadingFieldValuesRef.current = true;
		lastLoadedFieldValuesKeyRef.current = requestKey;

		try {
			const getValues = await field({
				loan_guid: params?.id,
				fields:
					"2,URLA.X73,URLA.X75,12,14,4000,4001,4002,4004,4005,4006,3,2400,VASUMM.X23,Log.MS.CurrentMilestone,1401,CX.LOCKREQUESTTIMESTAMP,2149,2151,3420,2202,2203,2160,TPO.X14,TPO.X62,TPO.X75,CX.PRODUCT.FAMILY.LOGAN,CX.INCOMEVERIFYTYPE,CX.DEBTSERVICECOVERAGERATIO,CX.DSCR.CALC,ENHANCEDCOND.X1,CX.CB.DPATH.YN",
				selectedOrganization,
				userDetails,
			}).unwrap();
			setFieldValues(getValues?.response);
			setOrgLoanChannel(
				getValues?.response?.find((o) => o.fieldId === "TPO.X14")?.value
			);

			const enhancedConditionValue =
				getValues?.response?.find((o) => o.fieldId === "ENHANCEDCOND.X1")
					?.value || "";
			store.dispatch(setEnhancedConditionStatus(enhancedConditionValue));
			await loadConditionTypeOptions(enhancedConditionValue);
		} catch (err) {
			// setError(err?.data?.message);
		} finally {
			isLoadingFieldValuesRef.current = false;
		}
	}, [field, loadConditionTypeOptions, params?.id, selectedOrganization, userDetails]);

	useEffect(() => {
		if (params?.id && userDetails && selectedOrganization !== undefined) {
			getFieldValues();
		}
	}, [params?.id, selectedOrganization, userDetails, getFieldValues]);

	const enhancedConditionStatus = useSelector(selectEnhancedConditionStatus);
	const isEnhancedCondition =
		String(enhancedConditionStatus ?? "")
			.trim()
			.toUpperCase() === "Y";

	const refreshConditionTypeOptions = useCallback(
		async () => {
			const enhancedConditionValue =
				String(enhancedConditionStatus ?? "").trim().toUpperCase() ||
				String(filterValue(fieldValues, "ENHANCEDCOND.X1") ?? "")
					.trim()
					.toUpperCase();

			const refreshedOptions = await loadConditionTypeOptions(
				enhancedConditionValue,
				{ forceRefresh: true }
			);

			if (Array.isArray(refreshedOptions) && drawerData) {
				setDrawerData((previousDrawerData) =>
					previousDrawerData
						? {
								...previousDrawerData,
								nextConditionsOptions: refreshedOptions,
						  }
						: previousDrawerData
				);
			}

			return refreshedOptions;
		},
		[drawerData, enhancedConditionStatus, fieldValues, loadConditionTypeOptions]
	);

	function a11yProps(index) {
		return {
			id: `simple-tab-${index}`,
			"aria-controls": `simple-tabpanel-${index}`,
		};
	}

	const handleTabChange = (event, value) => {
		setTabValue(value);
	};

	const toggleDrawer = (event, drawerType, data) => {
		if (drawerType !== undefined && drawerType !== "") {
			setDrawerType(drawerType);
			setDrawerData(data ?? null);
			handleOpenDrawer();
		} else {
			setDrawerType("");
			setDrawerData(null);
			handleCloseDrawer();
		}
	};

	const breadcrumbData = [
		{
			label: "Home",
			path: "/home",
		},
		{
			label: "Loan Pipeline",
			path: "/pipeline",
		},
		{
			label: !hideLoan && loanData?.loanNumber,
			path: "",
			handleClick: (e) => handleTabChange(e, false),
		},
	];

	const tabs = [
		{
			label: "Lock Information and Activities",
			isDisabled: false,
			content: (
				<LockInfoCard
					loanData={loanData}
					fieldData={fieldValues}
					toggleDrawer={toggleDrawer}
					handleTabChange={handleTabChange}
					// loading data modal
					handleOpenLoadingDataModal={handleOpenLoadingDataModal}
					handleCloseLoadingDataModal={handleCloseLoadingDataModal}
					setLoadingDataText={setLoadingDataText}
					// error modal
					handleOpenErrorModal={handleOpenErrorModal}
					setErrorModalText={setErrorModalText}
				/>
			),
		},
		{
			label: "Change Requests",
			isDisabled: false,
			content: (
				<ChangeRequestCard
					toggleDrawer={toggleDrawer}
					handleTabChange={handleTabChange}
					// loading data modal
					handleOpenLoadingDataModal={handleOpenLoadingDataModal}
					handleCloseLoadingDataModal={handleCloseLoadingDataModal}
					// error modal
					handleOpenCommonErrorModal={handleOpenCommonErrorModal}
				/>
			),
		},
		{
			label: "Condition & Document Tracker",
			content: (
			<DocumentsTrackerCard
				toggleDrawer={toggleDrawer}
				handleTabChange={handleTabChange}
				fieldData={fieldValues}
				loanGuid={resolvedLoanGuid}
				setSelectedCondition={setSelectedCondition}
				conditionTypeOptions={conditionTypeOptions}
				refreshConditionTypeOptions={refreshConditionTypeOptions}
				isConditionStatusLoaded={isConditionStatusLoaded}
				uploadProgressInfo={documentTrackerUploadProgress}
				setDocumentTrackerUploadProgress={
						setDocumentTrackerUploadProgress
					}
					// loading data modal
					handleOpenLoadingDataModal={handleOpenLoadingDataModal}
					handleCloseLoadingDataModal={handleCloseLoadingDataModal}
					setLoadingDataText={setLoadingDataText}
					// error modal
					handleOpenErrorModal={handleOpenCommonErrorModal}
				/>
			),
		},
		{
			label: "Important Dates",
			isDisabled: false,
			content: (
				<ImportantDatesCard
					handleTabChange={handleTabChange}
					// loading data modal
					handleOpenLoadingDataModal={handleOpenLoadingDataModal}
					handleCloseLoadingDataModal={handleCloseLoadingDataModal}
					setLoadingDataText={setLoadingDataText}
					// error modal
					handleOpenCommonErrorModal={handleOpenCommonErrorModal}
				/>
			),
		},
		selectedUserWithLoanChannel(
			userDetails,
			impersonatedUser,
			selectedOrganization
		)?.loan_channel !== LOAN_CHANNEL_CONSTANTS.NDC &&
			selectedUserWithLoanChannel(
				userDetails,
				impersonatedUser,
				selectedOrganization
			)?.loan_channel !== LOAN_CHANNEL_CONSTANTS.CORR && {
				label: "Appraisals",
				isDisabled: false,
				content: (
					<AppraisalsCard
						handleTabChange={handleTabChange}
						// loading data modal
						handleOpenLoadingDataModal={handleOpenLoadingDataModal}
						handleCloseLoadingDataModal={handleCloseLoadingDataModal}
						setLoadingDataText={setLoadingDataText}
						// error modal
						handleOpenCommonErrorModal={handleOpenCommonErrorModal}
					/>
				),
			},
		selectedUserWithLoanChannel(
			userDetails,
			impersonatedUser,
			selectedOrganization
		)?.loan_channel !== LOAN_CHANNEL_CONSTANTS.NDC &&
			selectedUserWithLoanChannel(
				userDetails,
				impersonatedUser,
				selectedOrganization
			)?.loan_channel !== LOAN_CHANNEL_CONSTANTS.CORR && {
				label: "Closing Information",
				isDisabled: false,
				content: (
					<ClosingInformationCard
						handleTabChange={handleTabChange}
						toggleDrawer={toggleDrawer}
						// loading data modal
						handleOpenLoadingDataModal={handleOpenLoadingDataModal}
						handleCloseLoadingDataModal={handleCloseLoadingDataModal}
						setLoadingDataText={setLoadingDataText}
						// error modal
						handleOpenCommonErrorModal={handleOpenCommonErrorModal}
					/>
				),
			},
		// {
		// 	label: "File Contact",
		// 	isDisabled: false,
		// 	content: (
		// 		<FileContactCard
		// 			handleTabChange={handleTabChange}
		// 			// loading data modal
		// 			handleOpenLoadingDataModal={handleOpenLoadingDataModal}
		// 			handleCloseLoadingDataModal={handleCloseLoadingDataModal}
		// 			setLoadingDataText={setLoadingDataText}
		// 			// error modal
		// 			handleOpenErrorModal={handleOpenErrorModal}
		// 			setErrorModalText={setErrorModalText}
		// 		/>
		// 	),
		// },
		(userDetails?.user_roles?.includes("admin") ||
			userDetails?.user_roles?.includes("executive") ||
			userDetails?.user_roles?.includes("AE") ||
			userDetails?.user_roles?.includes("AM") ||
			userDetails?.user_roles?.includes("manager") ||
			userDetails?.user_roles?.includes("PA") ||
			userDetails?.user_roles?.includes("assistant") ||
			selectedUserWithLoanChannel(
				userDetails,
				impersonatedUser,
				selectedOrganization
			)?.loan_channel === LOAN_CHANNEL_CONSTANTS.CB ||
			selectedUserWithLoanChannel(
				userDetails,
				impersonatedUser,
				selectedOrganization
			)?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
			selectedUserWithLoanChannel(
				userDetails,
				impersonatedUser,
				selectedOrganization
			)?.loan_channel === LOAN_CHANNEL_CONSTANTS.WHS) && {
			label: "Loan Contacts",
			isDisabled: false,
			content: (
				<LoanContactCard
					handleTabChange={handleTabChange}
					// loading data modal
					handleOpenLoadingDataModal={handleOpenLoadingDataModal}
					handleCloseLoadingDataModal={handleCloseLoadingDataModal}
					setLoadingDataText={setLoadingDataText}
					// error modal
					handleOpenCommonErrorModal={handleOpenCommonErrorModal}
				/>
			),
		},
		// {
		// 	label: "Request lock",
		// 	isDisabled: false,
		// 	content: (
		// 		<RequestLockCard
		// 			loanData={loanData}
		// 			handleTabChange={handleTabChange}
		// 			getFieldValuesOnLoanSummary={getFieldValues}
		// 		/>
		// 	),
		// },
	].filter(Boolean); // Remove any `false` values when a tab is conditional

	console.log("420 tab value:", tabValue);

	return (
		<Box>
			<CustomLayout breadcrumb={breadcrumbData} disabledOverflow={true}>
				{!hideLoan && (
					<Stack
						direction="column"
						className={styles.stackContainer}
						sx={{
							overflow: "hidden",
						}}
					>
						<LoanSummaryCard loanData={loanData} fieldData={fieldValues} />

						<Stack direction="column" className={styles.bodyContainer}>
							<Stack
								direction="row"
								className={`${styles.tabsContainer} tabStackContainer`}
							>
								{
									(console.log("290 tabs:", tabs),
									console.log("290 tab value:", tabValue))
								}
								<Tabs
									value={tabValue}
									onChange={handleTabChange}
									className={styles.tabContainer}
									TabIndicatorProps={{ style: { display: "none" } }}
								>
									{tabs.map((tab, index) => (
										<Tab
											key={index}
											className={styles.tabBtnContainer}
											component="div"
											label={
												<SecondaryButton
													text={tab.label}
													extraClass={
														tabValue === index
															? styles.selectedTabBtn
															: styles.tabBtn
													}
													disabled={tab.isDisabled}
												/>
											}
											disabled={tab.isDisabled}
											{...a11yProps(index)}
										/>
									))}
								</Tabs>
							</Stack>

							<Stack direction="column" className={styles.tabPanelContainer}>
								{tabs.map((tab, index) => (
									<TabPanel key={index} value={tabValue} index={index}>
										{tab.content}
									</TabPanel>
								))}

								<TabPanel value={tabValue} index={9}>
									<RequestLockCard
										loanData={loanData}
										handleTabChange={handleTabChange}
										getFieldValuesOnLoanSummary={getFieldValues}
									/>
								</TabPanel>
							</Stack>
						</Stack>
					</Stack>
				)}

				<TemporaryDrawer
					// drawer props
					drawerOpen={drawerOpen}
					drawerDirection="right"
					drawerType={drawerType}
					drawerData={drawerData}
					conditionTypeOptions={conditionTypeOptions}
					toggleDrawer={toggleDrawer}
					handleCloseDrawer={handleCloseDrawer}
					handleOpenProcessingModal={handleOpenProcessingModal}
					handleCloseProcessingModal={handleCloseProcessingModal}
					setProcessingText={setProcessingText}
					handleOpenSuccessModal={handleOpenSuccessModal}
					setSuccessText={setSuccessText}
					setDocumentTrackerUploadProgress={setDocumentTrackerUploadProgress}
					isConditionStatusLoaded={isConditionStatusLoaded}
					handleOpenErrorModal={handleOpenErrorModal}
					setErrorText={setErrorModalText}
					setErrorTitle={setErrorModalTitle}
					handleOpenLoadingDataModal={handleOpenLoadingDataModal}
					handleCloseLoadingDataModal={handleCloseLoadingDataModal}
					setLoadingDataText={setLoadingDataText}
					handleOpenCommonErrorModal={handleOpenCommonErrorModal}
					// request lock props
					loanData={loanData}
					fieldData={fieldValues}
					// condition tracking props
					selectedCondition={selectedCondition}
					setSelectedCondition={setSelectedCondition}
				/>
			</CustomLayout>

			{/* modals */}
			<LoadingDataModal open={loadingDataModal} text={loadingDataText} />
			<ProcessingModal open={processingModalVisible} text={processingText} />
			<SuccessModal
				open={successModalVisible}
				handleClose={handleCloseSuccessModal}
				text={successText}
			/>
			<ErrorModal
				open={errorModalVisible}
				handleClose={handleCloseErrorModal}
				text={errorModalText}
				title={errorModalTitle}
			/>
			<CommanErrorModal
				isOpen={commonErrorModalVisible}
				onClose={handleCloseCommonErrorModal}
				title={commonErrorData?.title}
				message={commonErrorData?.message}
				errorKey={commonErrorData?.error_key}
				errorCode={commonErrorData?.code}
				errorResponse={commonErrorData?.error_response}
			/>
			<NavigationModal
				open={navigationModalVisible}
				text={navigationModalText}
				confirmText="Okay"
				handleConfirm={handleNavigatePipeline}
			/>
		</Box>
	);
};

export default LoanSummary;
